import AsyncStorage from '@react-native-async-storage/async-storage';
import { APP_CONTAINER_ROUTES, INSIGHTS_HUB_ROUTES } from '@/navigators/routes';
import { InsightsHubStackParams } from '@/navigators/types';
import { PerfTiming, PerfTimingEvent } from '@/utils/perf-timing';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallback, useEffect } from 'react';
import { useNavigation } from '@react-navigation/native';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';

export const NAVIGATE_TO_CHAT_UUID_CARD_ON_LOAD_KEY =
  'navigateToChatUUIDCartOnLoad';

type Navigation = StackNavigationProp<
  InsightsHubStackParams,
  INSIGHTS_HUB_ROUTES.DraftDetails
>;

/**
 * Manages cross-screen navigation logic for automatically redirecting users to the cart from the Order Guide
 * Covers use cases such as when the:
 * - user is already in the same chat and can go directly to the cart
 * - user is in a different chat and needs to switch first, then get redirected to the cart
 */
export const useCartRedirect = () => {
  const { navigate } = useNavigation<Navigation>();
  const {
    selectedRestaurantUUID,
    selectRestaurant,
    restaurantUUIDToRelationshipMap,
  } = useAuthenticatedUserContext();
  const { fulfillmentHydrated } = useNewOrderActionsContext();

  // Automatically redirect to cart if key is set and context matches
  useEffect(() => {
    (async () => {
      const nextRestaurantUUID = await AsyncStorage.getItem(
        NAVIGATE_TO_CHAT_UUID_CARD_ON_LOAD_KEY,
      );

      if (!nextRestaurantUUID || !fulfillmentHydrated) {
        return;
      }

      const selectedChatUUID =
        restaurantUUIDToRelationshipMap[selectedRestaurantUUID].chatUUIDs[0];
      const targetChatUUID =
        restaurantUUIDToRelationshipMap[nextRestaurantUUID].chatUUIDs[0];

      if (selectedChatUUID === targetChatUUID) {
        await AsyncStorage.removeItem(NAVIGATE_TO_CHAT_UUID_CARD_ON_LOAD_KEY);
        navigate(APP_CONTAINER_ROUTES.NewOrder);
      }
    })();
  }, [
    fulfillmentHydrated,
    navigate,
    restaurantUUIDToRelationshipMap,
    selectedRestaurantUUID,
  ]);

  /**
   * Navigates the user to the cart (Order Summary Screen)
   * - If the given restaurant is already selected, navigates directly
   * - Otherwise, sets redirect key and selects the restaurant
   */
  const navigateToCart = useCallback(
    async (nextRestaurantUUID: string) => {
      if (nextRestaurantUUID === selectedRestaurantUUID) {
        PerfTiming.start(PerfTimingEvent.ORDER_SUMMARY_VISIBLE, {
          entryPoint: 'insights_hub_navigate_to_cart',
        });
        navigate(APP_CONTAINER_ROUTES.NewOrder);
      } else {
        await AsyncStorage.setItem(
          NAVIGATE_TO_CHAT_UUID_CARD_ON_LOAD_KEY,
          nextRestaurantUUID,
        );
        selectRestaurant(nextRestaurantUUID);
      }
    },
    [navigate, selectRestaurant, selectedRestaurantUUID],
  );

  return { navigateToCart };
};
